// ==============================================
// Admin Basemap Usage Page
// ==============================================
//
// Per-user, per-provider tile usage report: general browsing and
// pack-download tiles as separate, independently sortable columns for
// each basemap provider (Esri, Google, Bing, Sentinel-2, and Other for
// every source outside those four).
//
// Data: GET /api/admin/usage/basemaps?month=YYYY-MM — browse tiles from
// `esri_tile_request` metrics (LA live meter + VN direct-fetch counter),
// pack tiles from `download_token_consumed` joined to device_packs.
//
// Chart conventions follow the dataviz skill: fixed categorical palette
// validated for the slate surface (Esri #3987e5, Google #008300,
// Bing #d55181, Sentinel-2 #c98500 — adjacent CVD ΔE ≥ 8.4, normal ≥ 19.3),
// color follows the entity and is never re-assigned when providers are
// hidden; provider identity is also carried by column bands + labels,
// never color alone.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TableCellsIcon,
  ArrowsRightLeftIcon,
  GlobeAltIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';

// ==============================================
// Provider registry — slot colors follow the entity, never the rank
// ==============================================

interface ProviderDef {
  id: string;
  label: string;
  color: string;
  /** Offline packs only exist for LA-served sources. */
  packCapable: boolean;
}

const PROVIDERS: ProviderDef[] = [
  { id: 'esri', label: 'Esri', color: '#3987e5', packCapable: true },
  { id: 'google', label: 'Google', color: '#008300', packCapable: false },
  { id: 'bing', label: 'Bing', color: '#d55181', packCapable: false },
  { id: 'sentinel-2', label: 'Sentinel-2', color: '#c98500', packCapable: true },
  // Everything else the API reports: OSM, OpenTopoMap, and any unrecognised source.
  // Neutral, so it never reads as a fifth named provider.
  { id: 'other', label: 'Other', color: '#8b95a5', packCapable: true },
];

// Solid composite of slate-800/50 over slate-900 — sticky cells need an
// opaque background so scrolled columns don't show through.
const CARD_SOLID = '#172032';
const tint = (hex: string) => `${hex}12`; // ~7% wash

type Metric = 'split' | 'browse' | 'pack' | 'total';
type SortKey = '__user' | '__total' | string; // provider id | `${id}:b` | `${id}:p`

interface ReportUser {
  user_id: string;
  name: string | null;
  email: string;
  providers: Partial<Record<string, { browse: number; pack: number }>>;
  total_tiles: number;
}

// ==============================================
// Helpers
// ==============================================

const fmt = (n: number) => n.toLocaleString();
const fmtCompact = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 2)}M`
  : n >= 1_000 ? `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`
  : String(n);

function lastMonths(count: number): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push({
      value: d.toISOString().slice(0, 7),
      label: d.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    });
  }
  return out;
}

function split(u: ReportUser, providerId: string): { browse: number; pack: number } {
  return u.providers[providerId] ?? { browse: 0, pack: 0 };
}

function metricVal(u: ReportUser, providerId: string, metric: Metric): number {
  const s = split(u, providerId);
  return metric === 'browse' ? s.browse : metric === 'pack' ? s.pack : s.browse + s.pack;
}

// ==============================================
// Page
// ==============================================

export function AdminUsagePage() {
  const months = useMemo(() => lastMonths(6), []);
  const [month, setMonth] = useState(months[0].value);
  const [metric, setMetric] = useState<Metric>('split');
  const [enabled, setEnabled] = useState<Set<string>>(new Set(PROVIDERS.map((p) => p.id)));
  const [search, setSearch] = useState('');
  const [minTiles, setMinTiles] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>('__total');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);

  // The Esri free tier and price as set in Admin › Esri Services.
  const { data: esriConfig } = useQuery({
    queryKey: ['admin', 'esri', 'config'],
    queryFn: () => api.adminGetEsriConfig(),
  });

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['admin-basemap-usage', month],
    queryFn: () => api.adminGetBasemapUsage(month),
  });

  const providers = PROVIDERS.filter((p) => enabled.has(p.id));
  const isSplit = metric === 'split';
  const totalMetric: Metric = isSplit ? 'total' : metric;

  const userTotal = (u: ReportUser) =>
    providers.reduce((t, p) => t + metricVal(u, p.id, totalMetric), 0);

  const allUsers: ReportUser[] = useMemo(() => report?.users ?? [], [report]);

  const fleetTotal = useMemo(
    () => allUsers.reduce((t, u) => t + providers.reduce((s, p) => s + metricVal(u, p.id, totalMetric), 0), 0) || 1,
    [allUsers, providers, totalMetric],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = allUsers
      .filter((u) => !q || (u.name ?? '').toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .filter((u) => userTotal(u) >= minTiles);
    const dir = sortDir;
    return [...filtered].sort((a, b) => {
      if (sortKey === '__user') {
        const av = (a.name ?? a.email).toLowerCase();
        const bv = (b.name ?? b.email).toLowerCase();
        return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
      }
      let av: number, bv: number;
      if (sortKey === '__total') { av = userTotal(a); bv = userTotal(b); }
      else if (sortKey.endsWith(':b')) { av = split(a, sortKey.slice(0, -2)).browse; bv = split(b, sortKey.slice(0, -2)).browse; }
      else if (sortKey.endsWith(':p')) { av = split(a, sortKey.slice(0, -2)).pack; bv = split(b, sortKey.slice(0, -2)).pack; }
      else { av = metricVal(a, sortKey, totalMetric); bv = metricVal(b, sortKey, totalMetric); }
      return (av - bv) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, search, minTiles, sortKey, sortDir, providers.length, totalMetric]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDir(key === '__user' ? 1 : -1); }
  };

  const toggleProvider = (id: string) => {
    setEnabled((prev) => {
      if (prev.has(id) && prev.size === 1) return prev; // keep at least one
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (sortKey === id || sortKey.startsWith(`${id}:`)) { setSortKey('__total'); setSortDir(-1); }
      } else next.add(id);
      return next;
    });
  };

  const setMetricMode = (m: Metric) => {
    setMetric(m);
    // sort keys that only exist in one mode fall back to total
    if (m !== 'split' && /:b$|:p$/.test(sortKey)) { setSortKey('__total'); setSortDir(-1); }
    if (m === 'split' && PROVIDERS.some((p) => p.id === sortKey)) setSortKey(`${sortKey}:b`);
  };

  // ---- summary numbers ----
  const fleetBrowse = allUsers.reduce((t, u) => t + providers.reduce((s, p) => s + split(u, p.id).browse, 0), 0);
  const fleetPack = allUsers.reduce((t, u) => t + providers.reduce((s, p) => s + split(u, p.id).pack, 0), 0);
  const fleetAll = fleetBrowse + fleetPack;
  const providerTotals = PROVIDERS.map((p) => ({
    p,
    tiles: allUsers.reduce((t, u) => t + metricVal(u, p.id, 'total'), 0),
  }));
  const topProvider = [...providerTotals].sort((a, b) => b.tiles - a.tiles)[0];
  const esriTiles = providerTotals.find((t) => t.p.id === 'esri')?.tiles ?? 0;
  const FREE_TILES: number = esriConfig?.free_tiles_per_month ?? 2_000_000;
  const COST_PER_1K_USD: number = esriConfig?.cost_per_1000_tiles_usd ?? 0;
  const esriPct = FREE_TILES > 0 ? Math.min(100, (esriTiles / FREE_TILES) * 100) : 100;
  const esriOver = Math.max(0, esriTiles - FREE_TILES);
  const monthLabel = months.find((m) => m.value === month)?.label ?? month;

  const mixTotals = providers.map((p) => ({
    p,
    tiles: allUsers.reduce((t, u) => t + metricVal(u, p.id, totalMetric), 0),
  }));
  const mixSum = mixTotals.reduce((t, m) => t + m.tiles, 0) || 1;

  const sortArrow = (key: SortKey) =>
    sortKey === key ? (
      sortDir === -1 ? <ChevronDownIcon className="w-3 h-3 inline ml-0.5" /> : <ChevronUpIcon className="w-3 h-3 inline ml-0.5" />
    ) : null;

  const numCell = (v: number, na = false) =>
    na ? <span className="text-slate-600">n/a</span> : v === 0 ? <span className="text-slate-500">—</span> : fmt(v);

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* header */}
      <div>
        <h1 className="text-xl font-bold text-white">Basemap usage by user</h1>
        <p className="text-sm text-slate-400 mt-1 max-w-3xl">
          Tiles per basemap provider, attributed to user accounts from Licensing-Agent usage metrics —
          general browsing and pack-download tiles reported as separate columns. Click any column to sort.
        </p>
      </div>

      {/* filters */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 flex flex-wrap items-center gap-3">
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white"
          aria-label="Month"
        >
          {months.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>

        <div className="inline-flex bg-slate-900 border border-slate-600 rounded-lg p-0.5" role="group" aria-label="Usage type">
          {([
            ['split', 'Browse / Pack'],
            ['browse', 'Browsing'],
            ['pack', 'Packs'],
            ['total', 'Combined'],
          ] as [Metric, string][]).map(([m, label]) => (
            <button
              key={m}
              onClick={() => setMetricMode(m)}
              aria-pressed={metric === m}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                metric === m ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {PROVIDERS.map((p) => {
            const on = enabled.has(p.id);
            return (
              <button
                key={p.id}
                onClick={() => toggleProvider(p.id)}
                aria-pressed={on}
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium transition-opacity ${
                  on ? 'border-slate-600 bg-slate-900 text-white' : 'border-slate-700 bg-slate-900 text-slate-500 opacity-50'
                }`}
              >
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: on ? p.color : '#64748b' }} />
                {p.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1" />

        <select
          value={minTiles}
          onChange={(e) => setMinTiles(Number(e.target.value))}
          className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white"
          aria-label="Minimum tiles"
        >
          <option value={0}>All users</option>
          <option value={10_000}>≥ 10k tiles</option>
          <option value={100_000}>≥ 100k tiles</option>
          <option value={500_000}>≥ 500k tiles</option>
        </select>

        <div className="relative">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email…"
            className="bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-3 py-1.5 text-sm text-white w-56 placeholder:text-slate-500"
          />
        </div>
      </div>

      {/* stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <StatTile
          icon={TableCellsIcon}
          accent="#3987e5"
          label={`Fleet tiles · ${monthLabel}`}
          value={fmtCompact(fleetAll)}
          note={`across ${allUsers.length} users, all providers`}
        />
        <StatTile
          icon={ArrowsRightLeftIcon}
          accent="#199e70"
          label="Browsing vs packs"
          value={fleetAll ? `${Math.round((fleetBrowse / fleetAll) * 100)}% browse` : '—'}
          note={`${fmtCompact(fleetBrowse)} browsing · ${fmtCompact(fleetPack)} pack download`}
        />
        <StatTile
          icon={GlobeAltIcon}
          accent={topProvider?.p.color ?? '#3987e5'}
          label="Top provider"
          value={topProvider?.p.label ?? '—'}
          note={topProvider ? `${fmtCompact(topProvider.tiles)} tiles (${Math.round((topProvider.tiles / (fleetAll || 1)) * 100)}% of fleet)` : ''}
          swatch={topProvider?.p.color}
        />
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <p className="text-sm text-slate-400">Esri billable pool</p>
          <p className="text-2xl font-bold text-white mt-0.5 tabular-nums">
            {esriPct.toFixed(0)}<span className="text-sm text-slate-400 font-medium"> % of {fmtCompact(FREE_TILES)} free</span>
          </p>
          <p className="text-sm text-slate-500 mt-0.5 tabular-nums">
            {fmt(esriTiles)} Esri tiles
            {esriOver > 0
              ? <> · <span className="text-red-400 font-semibold">{fmtCompact(esriOver)} over</span> ≈ US${((esriOver / 1000) * COST_PER_1K_USD).toFixed(2)}</>
              : ' · US$0.00 overage'}
          </p>
          <div className="h-1.5 mt-2 rounded bg-slate-900 border border-slate-700 overflow-hidden">
            <div
              className={`h-full rounded ${esriPct >= 90 ? 'bg-red-500' : esriPct >= 60 ? 'bg-amber-500' : 'bg-green-600'}`}
              style={{ width: `${esriPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* fleet mix */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
        <h2 className="text-sm font-semibold text-white">Fleet mix by provider</h2>
        <p className="text-sm text-slate-400 mb-3">
          Share of {metric === 'browse' ? 'browsing' : metric === 'pack' ? 'pack-download' : 'browsing + pack'} tiles, {monthLabel}, all users
        </p>
        <div className="flex gap-0.5 h-6 rounded-md overflow-hidden" role="img" aria-label="Share of tiles by basemap provider">
          {mixTotals.map(({ p, tiles }) => (
            <div key={p.id} title={`${p.label}: ${fmt(tiles)} tiles (${((tiles / mixSum) * 100).toFixed(1)}%)`}
              style={{ flex: `${tiles} ${tiles} 0`, minWidth: tiles > 0 ? 3 : 0, backgroundColor: p.color }} />
          ))}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2.5">
          {mixTotals.map(({ p, tiles }) => (
            <span key={p.id} className="inline-flex items-center gap-1.5 text-sm text-slate-400">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
              {p.label} <b className="text-white tabular-nums">{((tiles / mixSum) * 100).toFixed(1)}%</b> · {fmtCompact(tiles)}
            </span>
          ))}
        </div>
      </div>

      {/* table */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="flex items-baseline justify-between px-4 pt-4 pb-2">
          <div>
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <UserGroupIcon className="w-4 h-4 text-slate-400" /> Users
            </h2>
            <p className="text-sm text-slate-400 mt-0.5">{monthLabel} · packs are only available for LA-served sources: Esri, Sentinel-2, and OSM / OpenTopoMap under Other</p>
          </div>
          <span className="text-sm text-slate-500 tabular-nums">{visible.length} of {allUsers.length} users</span>
        </div>

        {isLoading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Loading usage…</div>
        ) : error ? (
          <div className="p-10 text-center text-red-400 text-sm">Failed to load usage report.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 860, borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                {/* provider band row */}
                <tr>
                  <th className="sticky left-0 z-10" style={{ backgroundColor: CARD_SOLID }} />
                  {providers.map((p) => (
                    <th
                      key={p.id}
                      colSpan={isSplit ? 2 : 1}
                      className="px-3 pt-2 pb-1.5 text-left text-sm font-semibold text-white border-l border-slate-700"
                      style={{ backgroundColor: tint(p.color), borderBottom: `3px solid ${p.color}` }}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
                        {p.label}
                      </span>
                    </th>
                  ))}
                  <th colSpan={2} className="border-l border-slate-700" />
                </tr>
                {/* sortable metric row */}
                <tr className="text-[11px] uppercase tracking-wide text-slate-400">
                  <th
                    className="sticky left-0 z-10 px-3 py-1.5 text-left cursor-pointer hover:text-white border-b border-slate-700"
                    style={{ backgroundColor: CARD_SOLID }}
                    onClick={() => toggleSort('__user')}
                    aria-sort={sortKey === '__user' ? (sortDir === -1 ? 'descending' : 'ascending') : 'none'}
                  >
                    User{sortArrow('__user')}
                  </th>
                  {providers.map((p) =>
                    isSplit ? (
                      [
                        <th key={`${p.id}b`} onClick={() => toggleSort(`${p.id}:b`)}
                          className="px-3 py-1.5 text-right cursor-pointer hover:text-white border-l border-slate-700 border-b"
                          style={{ backgroundColor: tint(p.color) }}>
                          Browse{sortArrow(`${p.id}:b`)}
                        </th>,
                        <th key={`${p.id}p`} onClick={() => toggleSort(`${p.id}:p`)}
                          className="px-3 py-1.5 text-right cursor-pointer hover:text-white border-b border-slate-700"
                          style={{ backgroundColor: tint(p.color) }}>
                          Pack{sortArrow(`${p.id}:p`)}
                        </th>,
                      ]
                    ) : (
                      <th key={p.id} onClick={() => toggleSort(p.id)}
                        className="px-3 py-1.5 text-right cursor-pointer hover:text-white border-l border-slate-700 border-b"
                        style={{ backgroundColor: tint(p.color) }}>
                        {metric === 'browse' ? 'Browse' : metric === 'pack' ? 'Pack' : 'Tiles'}{sortArrow(p.id)}
                      </th>
                    ),
                  )}
                  <th onClick={() => toggleSort('__total')}
                    className="px-3 py-1.5 text-right cursor-pointer hover:text-white font-bold border-l border-b border-slate-700">
                    Total{sortArrow('__total')}
                  </th>
                  <th className="px-3 py-1.5 text-right border-b border-slate-700">% fleet</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={2 + providers.length * (isSplit ? 2 : 1) + 1} className="p-8 text-center text-slate-500">
                      No usage recorded for the current filters.
                    </td>
                  </tr>
                ) : (
                  visible.map((u) => {
                    const total = userTotal(u);
                    const share = (total / fleetTotal) * 100;
                    return (
                      <tr key={u.user_id} className="group">
                        <td className="sticky left-0 z-10 px-3 py-2 border-b border-r border-slate-700/60 min-w-[200px]"
                          style={{ backgroundColor: CARD_SOLID }}>
                          <div className="font-medium text-white">{u.name ?? u.email}</div>
                          {u.name && <div className="text-sm text-slate-500">{u.email}</div>}
                        </td>
                        {providers.map((p) => {
                          const s = split(u, p.id);
                          const title = `${p.label} — browsing ${fmt(s.browse)} · pack ${fmt(s.pack)}`;
                          return isSplit ? (
                            [
                              <td key={`${p.id}b`} title={title}
                                className="px-3 py-2 text-right tabular-nums text-slate-200 border-l border-b border-slate-700/60 group-hover:brightness-125"
                                style={{ backgroundColor: tint(p.color) }}>
                                {numCell(s.browse)}
                              </td>,
                              <td key={`${p.id}p`} title={title}
                                className="px-3 py-2 text-right tabular-nums text-slate-400 border-b border-slate-700/60 group-hover:brightness-125"
                                style={{ backgroundColor: tint(p.color) }}>
                                {numCell(s.pack, !p.packCapable)}
                              </td>,
                            ]
                          ) : (
                            <td key={p.id} title={title}
                              className="px-3 py-2 text-right tabular-nums text-slate-200 border-l border-b border-slate-700/60 group-hover:brightness-125"
                              style={{ backgroundColor: tint(p.color) }}>
                              {numCell(metricVal(u, p.id, metric), metric === 'pack' && !p.packCapable)}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-white border-l border-b border-slate-700/60">
                          {fmt(total)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-400 border-b border-slate-700/60">
                          <span className="inline-flex items-center gap-2">
                            <span className="w-14 h-1 rounded bg-slate-900 border border-slate-700 overflow-hidden">
                              <span className="block h-full bg-blue-500" style={{ width: `${Math.min(100, share * 4)}%` }} />
                            </span>
                            {share.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {visible.length > 0 && (
                <tfoot>
                  <tr className="text-sm font-semibold text-slate-400">
                    <td className="sticky left-0 z-10 px-3 py-2 border-r border-slate-700/60" style={{ backgroundColor: CARD_SOLID }}>
                      Column total ({visible.length} users)
                    </td>
                    {providers.map((p) =>
                      isSplit ? (
                        [
                          <td key={`${p.id}b`} className="px-3 py-2 text-right tabular-nums border-l border-slate-700/60" style={{ backgroundColor: tint(p.color) }}>
                            {fmtCompact(visible.reduce((t, u) => t + split(u, p.id).browse, 0))}
                          </td>,
                          <td key={`${p.id}p`} className="px-3 py-2 text-right tabular-nums" style={{ backgroundColor: tint(p.color) }}>
                            {p.packCapable ? fmtCompact(visible.reduce((t, u) => t + split(u, p.id).pack, 0)) : 'n/a'}
                          </td>,
                        ]
                      ) : (
                        <td key={p.id} className="px-3 py-2 text-right tabular-nums border-l border-slate-700/60" style={{ backgroundColor: tint(p.color) }}>
                          {fmtCompact(visible.reduce((t, u) => t + metricVal(u, p.id, metric), 0))}
                        </td>
                      ),
                    )}
                    <td className="px-3 py-2 text-right tabular-nums text-white border-l border-slate-700/60">
                      {fmtCompact(visible.reduce((t, u) => t + userTotal(u), 0))}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {((visible.reduce((t, u) => t + userTotal(u), 0) / fleetTotal) * 100).toFixed(1)}%
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ==============================================
// Stat tile
// ==============================================

function StatTile({ icon: Icon, accent, label, value, note, swatch }: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  accent: string;
  label: string;
  value: string;
  note: string;
  swatch?: string;
}) {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-slate-400">{label}</p>
          <p className="text-2xl font-bold text-white mt-0.5 tabular-nums truncate flex items-center gap-2">
            {swatch && <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: swatch }} />}
            {value}
          </p>
          <p className="text-sm text-slate-500 mt-0.5">{note}</p>
        </div>
        <div className="p-2.5 rounded-lg shrink-0" style={{ backgroundColor: `${accent}1a` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </div>
  );
}
